import { useEffect, useState } from "react";
import Container from "@/components/Container";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { Link } from "react-router-dom";
import {
  Database, Sparkles, Calendar, Star, GitBranch, Hammer,
  ShoppingBag, BadgeCheck, Mail, CheckSquare, Bookmark, Coins, Gauge,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, k, accent }) => (
  <div className="luxury-card p-4 md:p-6 min-w-0" data-testid={ADMIN.stat(k)}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.12em] md:tracking-[0.2em] text-ivory/50 leading-relaxed min-w-0 break-words">{label}</p>
      <Icon size={16} className={`shrink-0 ${accent ? "text-gold" : "text-ivory/40"}`} strokeWidth={1.5} />
    </div>
    <p className="mt-3 md:mt-5 text-2xl md:text-4xl font-serif text-ivory break-words">{value ?? 0}</p>
  </div>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [perf, setPerf] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats").then(r => setS(r.data)).catch(() => {});
    api.get("/portfolio/performance").then(r => setPerf(r.data?.summary || null)).catch(() => {});
  }, []);
  const st = s?.by_status || {};
  const fmtMoney = (n) => "£" + (Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });
  return (
    <Container className="py-8 md:py-12">
      <p className="overline">Overview</p>
      <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Dashboard</h1>
      <p className="text-ivory/50 mt-2 text-sm">Live state of the Rubys.Group acquisition engine.</p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Stat icon={Database} label="Total properties" value={s?.total_properties} k="total" accent />
        <Stat icon={Sparkles} label="New today" value={s?.new_today} k="today" />
        <Stat icon={Calendar} label="This week" value={s?.new_this_week} k="week" />
        <Stat icon={Star} label="Watchlisted" value={s?.watchlisted} k="watch" />
        <Stat icon={GitBranch} label="Shortlisted" value={st.shortlisted} k="shortlisted" />
        <Stat icon={GitBranch} label="Negotiating" value={st.negotiating} k="negotiating" />
        <Stat icon={BadgeCheck} label="Acquired" value={st.acquired} k="acquired" />
        <Stat icon={Hammer} label="In renovation" value={st.in_renovation} k="in_renovation" />
        <Stat icon={ShoppingBag} label="Listed for resale" value={st.listed_for_resale} k="listed_for_resale" />
        <Stat icon={BadgeCheck} label="Sold" value={st.sold} k="sold" />
        <Stat icon={Coins} label="Opportunity value (£)" value={s?.total_opportunity_value?.toLocaleString?.()} k="value" accent />
        <Stat icon={Gauge} label="Avg opportunity score" value={s?.avg_opportunity_score} k="score" />
        <Stat icon={Mail} label="Leads received" value={s?.leads_received} k="leads" />
        <Stat icon={CheckSquare} label="Tasks due" value={s?.tasks_due} k="tasks" />
        <Stat icon={Bookmark} label="Saved searches" value={s?.saved_searches} k="saved" />
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/admin/portfolio" className="luxury-card-ruby p-8 hover:border-gold transition-colors" data-testid="dash-portfolio-card">
          <p className="overline">Numbers — Live</p>
          <h3 className="mt-3 text-2xl font-serif text-ivory">Portfolio Performance</h3>
          {perf ? (
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ivory/50">Invested</p>
                <p className="font-serif text-xl text-ivory mt-1">{fmtMoney(perf.total_invested)}</p>
              </div>
              <div>
                <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ivory/50">Profit</p>
                <p className="font-serif text-xl text-gold mt-1">{fmtMoney(perf.total_profit)}</p>
              </div>
              <div>
                <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ivory/50">Avg ROI</p>
                <p className="font-serif text-xl text-ivory mt-1">{(perf.avg_roi_percentage || 0).toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ivory/50">Sold</p>
                <p className="font-serif text-xl text-ivory mt-1">{perf.sold_count || 0}</p>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-ivory/60 text-sm">Track offers, purchases, reno spend and resales — with live ROI.</p>
          )}
        </Link>
        <Link to="/admin/search-engine" className="luxury-card p-8 hover:border-gold transition-colors">
          <p className="overline">Discovery</p>
          <h3 className="mt-3 text-2xl font-serif text-ivory">Search Engine</h3>
          <p className="mt-2 text-ivory/60 text-sm">Generate Google-style search queries across portals, auctions, agents and councils.</p>
        </Link>
        <Link to="/admin/pipeline" className="luxury-card p-8 hover:border-gold transition-colors">
          <p className="overline">Deals</p>
          <h3 className="mt-3 text-2xl font-serif text-ivory">Deal Pipeline</h3>
          <p className="mt-2 text-ivory/60 text-sm">Move properties through reviewing, offers, acquisition, renovation and resale.</p>
        </Link>
      </div>
    </Container>
  );
}
